import { isAppLut } from 'legacy/lut/functions'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Roles } from 'clickNgo/configs/roles'
import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { validateFormData } from 'cng-common/features/form/helpers/validateFormData'
import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { Tooltip } from 'core-components/library/tooltip'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { showError, showSuccess } from 'onboarding/classes/services/notification'
import { useConvertToNumericId } from 'onboarding/hooks/useConvertToNumericId'
import { showErrorOnCatch } from 'onboarding/utils/errorResponse/showErrorOnCatch'

import { LNR_CHAMPION_FORM_KEY } from '../configs'

import { FormFields } from './FormFields'
import { useLNRChampion } from './hooks/useLNRChampion'
import { TLNRChampion } from './models'

import styles from './LNRData.module.scss'

interface IProps {
  requestId: string
  partnerId: string
  isDisabled: boolean
}

export const LNRChampion = ({
  requestId: requestIdAsString,
  partnerId,
  isDisabled: isDisabledPassed,
}: IProps) => {
  const requestId = useConvertToNumericId(requestIdAsString) || 0
  const dispatch = useAppDispatch()
  const user = useSelector(selectUser)
  const { fetched, isFetching, onSave, isSaving } = useLNRChampion(requestId)
  const [{ formData, modifiedFields }] = useGetFormData<TLNRChampion>(LNR_CHAMPION_FORM_KEY)
  const isFormChanged = useMemo(() => Boolean(modifiedFields.length), [modifiedFields])
  const callablePhoneAdditionalAttributes = useMemo(
    () => ({
      partnerId,
      contactProcessedInScout: isAppLut(),
    }),
    [partnerId]
  )
  const isDisabled =
    !user.roleIds.some(
      (role) => role === Roles.CONTRACT_OPERATIONS || role === Roles.SUPER_ADMIN_ROLE
    ) || isDisabledPassed

  const validateAndSave = useCallback(async () => {
    const errors = await validateFormData(formData, LNR_CHAMPION_FORM_KEY)
    if (errors.length) {
      showError('Some form data is invalid or incomplete')
    } else {
      const { error } = await onSave({ ...fetched, ...formData, RequestId: requestId })
      if (error) {
        showErrorOnCatch(error)
      } else {
        showSuccess('Saved')
      }
    }
  }, [formData, onSave, fetched, requestId])

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(LNR_CHAMPION_FORM_KEY))
    }
  }, [])

  if (isFetching || isSaving) {
    return <Loader />
  }

  return (
    <div className={styles.content}>
      <div className={styles.wrapper}>
        <FormFields
          formKey={LNR_CHAMPION_FORM_KEY}
          firstName={fetched.FirstName}
          lastName={fetched.LastName}
          email={fetched.Email}
          jobTitle={fetched.JobTitle}
          workPhonePrefix={fetched.WorkPhone_Prefix}
          workPhone={fetched.WorkPhone}
          mobilePhonePrefix={fetched.MobilePhone_Prefix}
          mobilePhone={fetched.MobilePhone}
          isDisabled={isDisabled}
          callablePhoneAdditionalAttributes={callablePhoneAdditionalAttributes}
        />
      </div>

      <div className={styles.btnWrapper}>
        <Tooltip
          tipContent={() => <span>No changes to save</span>}
          disabled={isFormChanged || isDisabled}
        >
          <Button onClick={validateAndSave} disabled={isDisabled || !isFormChanged}>
            Save
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}
